// EntityViews (§24.2, §29.4 WP5, §11.3): the visuals of every entity kind — a googly-eyed crate that
// wobbles down, banana peels that splat, bombs with fuse sparks, a pulsing glow and a floor-casting
// point light, flying plungers on Verlet ropes, ghost pebbles, the Crown Grab crown with its gold
// light, sugar cubes, punching dummies with a floating Dizzy sign. Each prop is one skinned mesh +
// one hull from a pooled set; `update()` allocates nothing once the pools are warm.
import {
  CanvasTexture, Euler, Group, Matrix4, Mesh, MeshBasicMaterial, PointLight, SRGBColorSpace, Vector3,
  type Object3D, type SkinnedMesh,
} from 'three';
import { MAX_BEANS, NO_SLOT, TUNE } from '@shared/constants';
import { EV, type SimEvent } from '@shared/events';
import { BS, EK } from '@shared/ids';
import type { BeanView, EntityView, FrameView } from '../../game/FrameView';
import { placeAt, yawForFacing } from '../../core/space';
import { clamp, len2, len3, Pupil, Spring } from '../bean/anim';
import { VerletChain } from '../bean/VerletChain';
import { OUTLINE_WIDTH, libraryShared, type MaterialLibrary } from '../materials/library';
import { createPartMaterials, partDepthMaterial, partHullMaterial, type PartMaterials } from '../materials/partMaterial';
import { PartSkin } from '../materials/partSkin';
import { EP, PROP_BONES, propGeometry, ROPE_BONES, ROPE_RINGS, signGeometry, type PropKind } from './entityGeometry';

export interface EntityViews {
  readonly root: Object3D;
  update(view: FrameView): void;
  dispose(): void;
  /** Additive (WP5): sim events — dummy hits wobble, plunger hits stick the cup, bombs thump down. */
  onSim?(ev: SimEvent, view: FrameView): void;
}

const KIND: Readonly<Record<EK, PropKind>> = {
  [EK.CRATE]: 'crate', [EK.PEEL]: 'peel', [EK.BOMB]: 'bomb', [EK.PLUNGER]: 'plunger',
  [EK.PEBBLE]: 'pebble', [EK.CROWN]: 'crown', [EK.CUBE]: 'cube', [EK.DUMMY]: 'dummy',
};
/** Warm pool sizes (field maximums): no allocation in normal play. */
const WARM: Readonly<Record<PropKind, number>> = { crate: 3, peel: 6, bomb: 3, plunger: 2, pebble: 3, crown: 1, cube: 4, dummy: 2, rope: 0 };
const ROPE_POOL = 4;
const PENDING_SCALE = 0.7;
const LIFE_BLINK_TICKS = 120;
const LIGHT = { bomb: '#FF4A3A', crown: '#FFD24A', distance: 5, decay: 2 } as const;

const mA = new Matrix4();
const mB = new Matrix4();
const mR = new Matrix4();
const mS = new Matrix4();
const mT = new Matrix4();
const vA = new Vector3();
const eul = new Euler();

/** Stable per-id hash in [0, 1). */
function hash01(id: number): number {
  const x = Math.sin(id * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

class Prop {
  readonly root = new Group();
  readonly skin = new PartSkin(PROP_BONES);
  readonly pm: PartMaterials;
  readonly mesh: SkinnedMesh;
  readonly hull: SkinnedMesh;
  readonly squash = new Spring(1, 320, 12);
  readonly wobX = new Spring(0, 70, 3.2);
  readonly wobZ = new Spring(0, 70, 3.2);
  readonly appear = new Spring(1, 260, 11);
  readonly pupilL = new Pupil(220, 8, 0.045);
  readonly pupilR = new Pupil(220, 8, 0.045);
  id = -1;
  /** Frame stamp of the last view that listed this prop's entity. */
  seen = -1;
  prevZ = 0;
  prevVx = 0;
  prevVy = 0;
  prevData2 = 0;
  spin = 0;
  phase = 0;
  readonly sign: DizzySign | null = null;

  constructor(readonly kind: PropKind, lib: MaterialLibrary, lo: boolean) {
    const g = propGeometry(kind, lo);
    this.pm = createPartMaterials(lib, null);
    this.mesh = this.skin.mesh(g.geometry, this.pm.main, `prop:${kind}`);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.customDepthMaterial = partDepthMaterial(lib);
    this.hull = this.skin.mesh(g.hull, partHullMaterial(lib, OUTLINE_WIDTH.prop), 'outline');
    this.root.add(this.mesh, this.hull);
    this.root.name = `entity:${kind}`;
    for (let i = 0; i < PROP_BONES; i++) this.skin.hide(i);
    this.pm.u.gloss.value = kind === 'cube' || kind === 'bomb' || kind === 'crown' ? 0.9 : 0.5;
    if (kind === 'dummy') {
      this.sign = new DizzySign();
      this.root.add(this.sign.mesh);
    }
  }

  setGeometry(lo: boolean): void {
    const g = propGeometry(this.kind, lo);
    this.mesh.geometry = g.geometry;
    this.hull.geometry = g.hull;
  }

  begin(e: EntityView): void {
    this.id = e.id;
    this.prevZ = e.z;
    this.prevVx = e.vx;
    this.prevVy = e.vy;
    this.prevData2 = e.data2;
    this.spin = hash01(e.id) * Math.PI * 2;
    this.phase = 0;
    this.squash.reset(1);
    this.wobX.reset(0);
    this.wobZ.reset(0);
    this.pupilL.reset();
    this.pupilR.reset();
    this.pm.u.seed.value = hash01(e.id) * 10;
    this.pm.u.fade.value = 0;
    // Peels splat in (§11.3); everything else simply appears.
    this.appear.reset(this.kind === 'peel' ? 0.3 : 1);
    if (this.kind === 'peel') this.appear.v = 5;
  }

  /** Googly eyes centered at (±dx, y, z) in prop space (radius r), parented by `parent`. */
  eyes(parent: Matrix4, dx: number, y: number, z: number, r: number, dt: number, ax: number, ay: number): void {
    this.pupilL.limit = r * 0.48;
    this.pupilR.limit = r * 0.48;
    this.pupilL.step(dt, ax, ay, 0, -r * 0.15);
    this.pupilR.step(dt, ax, ay, 0, -r * 0.15);
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1;
      const p = i === 0 ? this.pupilL : this.pupilR;
      mA.makeTranslation(side * dx, y, z);
      mT.multiplyMatrices(parent, mA);
      this.skin.set(i === 0 ? EP.EYE_L : EP.EYE_R, mT);
      const pz = Math.sqrt(Math.max(0, r * r - p.x * p.x - p.y * p.y)) - 0.003;
      mA.makeTranslation(side * dx + p.x, y + p.y, z + pz);
      mT.multiplyMatrices(parent, mA);
      this.skin.set(i === 0 ? EP.PUPIL_L : EP.PUPIL_R, mT);
    }
  }
}

/** The punching dummy's floating "DIZZY nn%" placard (a CanvasTexture redrawn only on change). */
class DizzySign {
  readonly mesh: Mesh;
  private readonly canvas: HTMLCanvasElement | null;
  private readonly tex: CanvasTexture | null;
  private value = -1;

  constructor() {
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    this.canvas = canvas;
    if (canvas) {
      canvas.width = 256;
      canvas.height = 150;
    }
    this.tex = canvas ? new CanvasTexture(canvas) : null;
    if (this.tex) {
      this.tex.colorSpace = SRGBColorSpace;
      this.tex.anisotropy = 4;
    }
    this.mesh = new Mesh(signGeometry(), new MeshBasicMaterial({ map: this.tex, transparent: true, toneMapped: false, depthWrite: false }));
    this.mesh.name = 'dizzySign';
    this.mesh.renderOrder = 3;
  }

  set(dizzy: number): void {
    const v = Math.round(clamp(dizzy, 0, 100));
    if (v === this.value || !this.canvas || !this.tex) return;
    this.value = v;
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.clearRect(0, 0, w, h);
    const col = v >= 70 ? '#FF4D4D' : v >= 40 ? '#FFC93C' : '#3DDC97';
    // Sticker: ink drop shadow, cream card, ink border.
    ctx.fillStyle = '#2A1A3A';
    roundRect(ctx, 10, 18, w - 20, h - 26, 26);
    ctx.fill();
    ctx.fillStyle = '#FFF6E9';
    roundRect(ctx, 10, 8, w - 20, h - 26, 26);
    ctx.fill();
    ctx.lineWidth = 8;
    ctx.strokeStyle = '#2A1A3A';
    ctx.stroke();
    ctx.fillStyle = '#2A1A3A';
    ctx.textAlign = 'center';
    ctx.font = '800 26px Nunito, sans-serif';
    ctx.fillText('DIZZY', w / 2, 44);
    ctx.font = '62px "Lilita One", Nunito, sans-serif';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 10;
    ctx.strokeText(`${v}%`, w / 2, 106);
    ctx.fillStyle = col;
    ctx.fillText(`${v}%`, w / 2, 106);
    this.tex.needsUpdate = true;
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** A Verlet rope between two world points (the plunger line, §11.3). */
class Rope {
  readonly skin = new PartSkin(ROPE_BONES);
  readonly mesh: SkinnedMesh;
  readonly hull: SkinnedMesh;
  readonly chain = new VerletChain(10, 0.2, 0.9, -7);
  private fresh = true;

  constructor(lib: MaterialLibrary, lo: boolean, pm: PartMaterials) {
    const g = propGeometry('rope', lo);
    this.mesh = this.skin.mesh(g.geometry, pm.main, 'plungerRope');
    this.hull = this.skin.mesh(g.hull, partHullMaterial(lib, OUTLINE_WIDTH.bean), 'outline');
    this.mesh.frustumCulled = false;
    this.hull.frustumCulled = false;
    this.skin.setTS(EP.BODY, 0, 0, 0, 1);
    this.chain.pinFirst = true;
    this.chain.pinLast = true;
  }

  pose(ax: number, ay: number, az: number, bx: number, by: number, bz: number, dt: number): void {
    const d = len3(bx - ax, by - ay, bz - az);
    const c = this.chain;
    c.segment = Math.max(0.02, (d * 1.06) / (c.n - 1));
    if (this.fresh) {
      c.reset(ax, ay, az, bx - ax, by - ay, bz - az);
      this.fresh = false;
    }
    c.setPoint(0, ax, ay, az);
    c.setPoint(c.n - 1, bx, by, bz);
    c.step(dt);
    c.poseTube(this.skin, EP.ROPE0, ROPE_RINGS, 0.022, 0.022);
  }

  show(on: boolean): void {
    this.mesh.visible = on;
    this.hull.visible = on;
    if (!on) this.fresh = true;
  }
}

class EntityViewsImpl implements EntityViews {
  readonly root = new Group();
  private readonly lib: MaterialLibrary;
  private readonly pools = new Map<PropKind, Prop[]>();
  private readonly live = new Map<number, Prop>();
  /** The live props as an array, for allocation-free iteration. */
  private readonly liveList: Prop[] = [];
  private frame = 0;
  private readonly ropes: Rope[] = [];
  private readonly ropePm: PartMaterials;
  private readonly lights: PointLight[] = [];
  private lo: boolean;
  private time = 0;

  constructor(lib: MaterialLibrary) {
    this.lib = lib;
    this.root.name = 'entities';
    this.lo = lowDetail(lib);
    for (const kind of Object.keys(WARM) as PropKind[]) {
      const list: Prop[] = [];
      for (let i = 0; i < WARM[kind]; i++) list.push(this.make(kind));
      this.pools.set(kind, list);
    }
    this.ropePm = createPartMaterials(lib, null);
    this.ropePm.u.gloss.value = 0.2;
    for (let i = 0; i < ROPE_POOL; i++) {
      const r = new Rope(lib, this.lo, this.ropePm);
      r.show(false);
      this.ropes.push(r);
      this.root.add(r.mesh, r.hull);
    }
    // §12.3: at most two dynamic point lights (a lit bomb and the crown), Medium and up. They stay
    // in the scene at intensity 0 when unused, so the light count (and every program) never changes.
    for (const color of [LIGHT.bomb, LIGHT.crown]) {
      const l = new PointLight(color, 0, LIGHT.distance, LIGHT.decay);
      l.castShadow = false;
      this.lights.push(l);
    }
    this.syncLights();
  }

  private make(kind: PropKind): Prop {
    const p = new Prop(kind, this.lib, this.lo);
    p.root.visible = false;
    return p;
  }

  private syncLights(): void {
    const on = this.lib.quality === 'high' || this.lib.quality === 'medium';
    for (let i = 0; i < this.lights.length; i++) {
      const l = this.lights[i];
      if (on && !l.parent) this.root.add(l);
      else if (!on && l.parent) l.removeFromParent();
    }
  }

  private acquire(kind: PropKind): Prop {
    const pool = this.pools.get(kind);
    const p = pool && pool.length > 0 ? (pool.pop() as Prop) : this.make(kind);
    this.root.add(p.root);
    return p;
  }

  private release(p: Prop): void {
    p.root.removeFromParent();
    p.root.visible = false;
    p.id = -1;
    this.pools.get(p.kind)?.push(p);
  }

  /** Removes a live prop (its entity is gone) and returns it to its pool. */
  private drop(p: Prop): void {
    this.live.delete(p.id);
    const i = this.liveList.indexOf(p);
    if (i >= 0) {
      this.liveList[i] = this.liveList[this.liveList.length - 1];
      this.liveList.pop();
    }
    this.release(p);
  }

  onSim(ev: SimEvent): void {
    if (ev.type !== EV.DUMMY_HIT && ev.type !== EV.BOMB_LAND) return;
    const p = this.live.get(ev.c);
    if (!p) return;
    if (ev.type === EV.DUMMY_HIT) {
      p.wobX.v += 9;
      p.squash.v -= 3;
      p.pupilL.kick(0.4, 0.3);
      p.pupilR.kick(0.4, 0.3);
    } else p.squash.v -= 4;
  }

  update(view: FrameView): void {
    const dt = clamp(view.dt, 0, 0.1);
    this.time += dt;
    this.frame++;
    libraryShared(this.lib).time.value = view.now / 1000;
    const lo = lowDetail(this.lib);
    if (lo !== this.lo) {
      this.lo = lo;
      this.pools.forEach(setPoolGeometry, lo);
      for (let i = 0; i < this.liveList.length; i++) this.liveList[i].setGeometry(lo);
    }
    this.syncLights();
    for (let i = 0; i < this.lights.length; i++) this.lights[i].intensity = 0;

    let ropeIndex = 0;
    for (let i = 0; i < view.ents.length; i++) {
      const e = view.ents[i];
      const kind = KIND[e.kind];
      if (!kind) continue;
      let p = this.live.get(e.id);
      if (p && p.kind !== kind) {
        this.drop(p);
        p = undefined;
      }
      if (!p) {
        p = this.acquire(kind);
        p.begin(e);
        this.live.set(e.id, p);
        this.liveList.push(p);
      }
      p.seen = this.frame;
      this.pose(p, e, view, dt);
      if (kind === 'plunger' && ropeIndex < this.ropes.length && this.plungerRope(e, view, dt, this.ropes[ropeIndex])) ropeIndex++;
    }
    for (let i = this.liveList.length - 1; i >= 0; i--) {
      const p = this.liveList[i];
      if (p.seen !== this.frame) this.drop(p);
    }
    // Yank ropes between YANKED partners (§3.3 plunger).
    for (let s = 0; s < MAX_BEANS && ropeIndex < this.ropes.length; s++) {
      const a = view.beans[s];
      if (!a.active || a.state !== BS.YANKED || a.partner === NO_SLOT || a.partner >= MAX_BEANS || a.partner < s) continue;
      const b = view.beans[a.partner];
      if (!b.active) continue;
      const r = this.ropes[ropeIndex++];
      r.show(true);
      handPoint(a, b.x - a.x, b.y - a.y, vA);
      const ax = vA.x;
      const ay = vA.y;
      const az = vA.z;
      handPoint(b, a.x - b.x, a.y - b.y, vA);
      r.pose(ax, ay, az, vA.x, vA.y, vA.z, dt);
    }
    for (let i = ropeIndex; i < this.ropes.length; i++) this.ropes[i].show(false);
  }

  /** The rope from the thrower's hand to a flying plunger's handle; false when there is no thrower. */
  private plungerRope(e: EntityView, view: FrameView, dt: number, r: Rope): boolean {
    if (e.owner >= MAX_BEANS) return false;
    const o = view.beans[e.owner];
    if (!o.active) return false;
    r.show(true);
    const sp = len2(e.vx, e.vy) || 1;
    handPoint(o, e.x - o.x, e.y - o.y, vA);
    r.pose(vA.x, vA.y, vA.z, e.x - (e.vx / sp) * 0.42, e.z, e.y - (e.vy / sp) * 0.42, dt);
    return true;
  }

  private pose(p: Prop, e: EntityView, view: FrameView, dt: number): void {
    const u = p.pm.u;
    const sk = p.skin;
    const t = this.time;
    const sq = p.squash.step(dt);
    p.appear.target = 1;
    const ap = p.appear.step(dt);
    const landed = p.prevZ > 0.06 && e.z <= 0.06;
    const ax = dt > 0 ? (e.vx - p.prevVx) / dt : 0;
    const ay = dt > 0 ? (e.vy - p.prevVy) / dt : 0;
    p.prevVx = e.vx;
    p.prevVy = e.vy;
    let visible = true;
    // Lifetime end: blink for the last 2 s (crates and peels poof at 1200 ticks).
    const life = e.kind === EK.CRATE ? TUNE.crate.lifeTicks : e.kind === EK.PEEL ? TUNE.banana.lifeTicks : 0;
    u.fade.value = life > 0 && e.t > life - LIFE_BLINK_TICKS && Math.sin(t * 30) > 0 ? 0.65 : 0;

    placeAt(p.root, e.x, e.y, e.z);
    p.root.rotation.set(0, 0, 0);
    p.root.scale.setScalar(e.pending ? PENDING_SCALE : 1);
    switch (p.kind) {
      case 'crate': {
        // §11.3: wobbles down with googly eyes; squashes on landing; idles with a breathing wobble.
        const falling = e.z > 0.06;
        if (landed) {
          p.squash.v -= 7;
          p.wobX.v += 2;
          p.pupilL.kick(0, -0.6);
          p.pupilR.kick(0, -0.6);
        }
        p.wobX.target = falling ? 0.16 * Math.sin(t * 7 + p.spin) : 0;
        p.wobZ.target = falling ? 0.12 * Math.sin(t * 5.3 + p.spin) : 0.02 * Math.sin(t * 1.5 + p.spin);
        p.root.rotation.set(p.wobX.step(dt), (hash01(e.id) - 0.5) * 0.6, p.wobZ.step(dt));
        const s = clamp(sq, 0.6, 1.4) * (falling ? 1.08 : 1);
        const w = 1 / Math.sqrt(s);
        mS.makeScale(w, s, w);
        sk.set(EP.BODY, mS);
        const open = 0.35 + 0.12 * Math.sin(t * 3 + p.spin) + (falling ? 0.25 * Math.sin(t * 13) : 0);
        for (let i = 0; i < 2; i++) {
          const side = i === 0 ? -1 : 1;
          mA.makeTranslation(side * 0.46 * w, 0.78 * s, 0);
          mR.makeRotationZ(-side * open);
          mB.multiplyMatrices(mA, mR);
          sk.set(i === 0 ? EP.FLAP_L : EP.FLAP_R, mB);
        }
        mS.identity();
        p.eyes(mS, 0.17 * w, 0.5 * s, 0.46 * w + 0.045, 0.14, dt, ax * 0.3, falling ? 30 : -ay * 0.2);
        break;
      }
      case 'peel': {
        p.root.rotation.set(0, hash01(e.id) * Math.PI * 2, 0);
        const s = Math.max(0.05, ap) * 1.3;
        mS.makeScale(s, Math.min(1.8, 2.6 - s), s);
        sk.set(EP.BODY, mS);
        break;
      }
      case 'bomb': {
        // §11.3: lit-fuse sparks, a red glow pulsing at the beep rate, a floor-casting point light.
        const lit = e.data2 === 1;
        const carried = e.data !== NO_SLOT && e.data < MAX_BEANS;
        const remain = lit ? clamp(e.timer / TUNE.bomb.fuseTicks, 0, 1) : 1;
        p.phase += (lit ? 1.6 + 9 * Math.pow(1 - remain, 1.6) : 1.2) * dt;
        const beat = Math.pow(0.5 + 0.5 * Math.cos(p.phase * Math.PI * 2), 4);
        u.potato.value = beat * (lit ? 0.6 + 0.4 * (1 - remain) : 0.25);
        u.spark.value = 0.5 + 0.5 * Math.sin(t * 57) * Math.sin(t * 31);
        if (landed) p.squash.v -= 5;
        if (!lit && !carried && e.z > 0.06) {
          p.spin += dt * 9;
          p.root.rotation.set(p.spin, Math.atan2(e.vx, e.vy), 0);
        } else p.root.rotation.set(0, (hash01(e.id) - 0.5) * 0.8, 0.06 * Math.sin(t * 20) * beat);
        const s = clamp(sq, 0.7, 1.3) * (1 + 0.07 * beat);
        mB.makeScale(s, s, s);
        mB.setPosition(0, 0.35 * s, 0);
        sk.set(EP.BODY, mB);
        const sp = 0.7 + 0.6 * u.spark.value;
        mA.makeTranslation(0.15, 0.53, 0.05);
        mR.makeRotationZ(t * 11);
        mS.makeScale(sp, sp, 1);
        mT.multiplyMatrices(mB, mA).multiply(mR).multiply(mS);
        sk.set(EP.SPARK, mT);
        p.eyes(mB, 0.12, 0.06, 0.3, 0.09, dt, ax, ay + beat * 40);
        const l = this.lights[0];
        if (l.parent && l.intensity === 0) {
          // Inside the bomb: the floor around it glows, the bomb itself stays a dark silhouette.
          l.position.set(p.root.position.x, p.root.position.y + 0.3, p.root.position.z);
          l.intensity = (lit ? 2 + 6 * beat : 1.2) * (0.85 + 0.15 * Math.sin(t * 43));
        }
        break;
      }
      case 'plunger': {
        const sp = len2(e.vx, e.vy);
        p.root.rotation.set(0.05 * Math.sin(t * 25), sp > 0.1 ? yawForFacing(Math.atan2(e.vy, e.vx)) : 0, 0);
        mS.identity();
        sk.set(EP.BODY, mS);
        break;
      }
      case 'pebble': {
        p.spin += dt * 4;
        p.root.rotation.set(p.spin, p.spin * 0.7, 0);
        mS.makeTranslation(0, 0.16, 0);
        sk.set(EP.BODY, mS);
        break;
      }
      case 'crown': {
        // Held crowns ride the holder's head (the rig draws them); the gold light follows the holder.
        const holder = e.data < MAX_BEANS ? view.beans[e.data] : null;
        const l = this.lights[1];
        if (holder && holder.active && holder.holdsCrown) {
          visible = false;
          if (l.parent) {
            l.position.set(holder.x, holder.z + 1.9 * (holder.radius > 0 ? holder.radius / TUNE.bean.radius : 1), holder.y);
            l.intensity = 3.5 + 0.8 * Math.sin(t * 4);
          }
          break;
        }
        const flying = len2(e.vx, e.vy) > 0.5 || Math.abs(e.vz) > 0.5;
        p.spin += dt * (flying ? 9 : 0.9);
        p.root.rotation.set(flying ? 0.4 * Math.sin(t * 10) : 0, p.spin, flying ? 0.3 : 0.08 * Math.sin(t * 1.3));
        mS.makeTranslation(0, 0.06 * Math.sin(t * 2.2), 0);
        sk.set(EP.BODY, mS);
        if (l.parent) {
          l.position.set(p.root.position.x, p.root.position.y + 0.15, p.root.position.z);
          l.intensity = 3 + 0.8 * Math.sin(t * 4);
        }
        break;
      }
      case 'cube': {
        if (e.timer > 0 || e.z <= TUNE.edge.killZ) {
          visible = false;
          break;
        }
        const dropping = e.data2 === 1 && e.z > 0.06;
        if (landed) {
          p.squash.v -= 6;
          p.wobX.v += 2.5;
        }
        p.wobX.target = dropping ? 0.15 * Math.sin(t * 6 + p.spin) : clamp(-ay * 0.004, -0.12, 0.12);
        p.wobZ.target = dropping ? 0.12 * Math.sin(t * 4.7 + p.spin) : clamp(ax * 0.004, -0.12, 0.12);
        p.root.rotation.set(p.wobX.step(dt), hash01(e.id) * Math.PI * 2, p.wobZ.step(dt));
        const s = clamp(sq, 0.6, 1.4);
        mS.makeScale(1 / Math.sqrt(s), s, 1 / Math.sqrt(s));
        sk.set(EP.BODY, mS);
        break;
      }
      case 'dummy': {
        if (e.timer > 0 || e.z <= TUNE.edge.killZ) {
          visible = false;
          break;
        }
        // The bag wobbles on its spring: knocked by hits (its Dizzy rises) and by its own sliding.
        if (e.data2 > p.prevData2) {
          p.wobX.v += 7 + 0.05 * e.data2;
          p.squash.v -= 3;
          p.pupilL.kick(0.5, 0.4);
          p.pupilR.kick(-0.4, 0.5);
        }
        p.prevData2 = e.data2;
        p.wobX.v += clamp(-ay * 0.001, -0.15, 0.15);
        p.wobZ.v += clamp(ax * 0.001, -0.15, 0.15);
        const wx = clamp(p.wobX.step(dt), -0.6, 0.6);
        const wz = clamp(p.wobZ.step(dt), -0.6, 0.6);
        const s = clamp(sq, 0.7, 1.3);
        sk.set(EP.BASE, mR.identity());
        mR.makeRotationFromEuler(eul.set(wx * 0.5, 0, wz * 0.5));
        mA.makeTranslation(0, 0.1, 0);
        mB.multiplyMatrices(mA, mR);
        sk.set(EP.POST, mB);
        mR.makeRotationFromEuler(eul.set(wx, 0, wz));
        mA.makeTranslation(0, 0.36, 0);
        mS.makeScale(1 / Math.sqrt(s), s, 1 / Math.sqrt(s));
        mB.multiplyMatrices(mA, mR).multiply(mS);
        sk.set(EP.BODY, mB);
        p.eyes(mB, 0.13, 0.66, 0.36, 0.08, dt, ax + wz * 60, -ay + wx * 60);
        if (p.sign) {
          p.sign.set(e.data2);
          p.sign.mesh.scale.setScalar(1.7);
          p.sign.mesh.position.set(wz * 0.4, 1.85 + 0.04 * Math.sin(t * 2), wx * 0.4);
          p.sign.mesh.rotation.set(-0.5, 0, 0);
        }
        break;
      }
      default:
        break;
    }
    p.root.visible = visible;
    p.prevZ = e.z;
  }

  dispose(): void {
    this.root.removeFromParent();
    for (const list of this.pools.values()) for (const p of list) p.pm.dispose();
    for (const p of this.liveList) p.pm.dispose();
    this.ropePm.dispose();
  }

  /** Test introspection: live props and the point-light intensities. */
  get debug(): { live: number; visible: number; lights: number[]; kinds: string[] } {
    return {
      live: this.liveList.length,
      visible: this.liveList.filter((p) => p.root.visible).length,
      lights: this.lights.filter((l) => l.parent).map((l) => l.intensity),
      kinds: this.liveList.filter((p) => p.root.visible).map((p) => p.kind),
    };
  }
}

function setPoolGeometry(this: boolean, list: Prop[]): void {
  for (let i = 0; i < list.length; i++) list[i].setGeometry(this);
}

function lowDetail(lib: MaterialLibrary): boolean {
  return lib.quality === 'low' || lib.quality === 'potato';
}

/** Approximate world position of a bean's hand reaching toward (dx, dy) (sim plane). */
function handPoint(b: BeanView, dx: number, dy: number, out: Vector3): Vector3 {
  const d = len2(dx, dy) || 1;
  const size = b.radius > 0 ? b.radius / TUNE.bean.radius : 1;
  return out.set(b.x + (dx / d) * 0.45 * size, b.z + 0.62 * size, b.y + (dy / d) * 0.45 * size);
}

export function createEntityViews(lib: MaterialLibrary): EntityViews {
  return new EntityViewsImpl(lib);
}
